import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  HelpCircle,
  FileText,
  ShieldCheck,
  Activity,
  FileSignature,
  DollarSign,
  MessageCircle,
  PlusCircle,
  AlertTriangle
} from "lucide-react";

const AjudaPage = () => {
  // Estado para controlar qual item está aberto (null = nenhum)
  const [openIndex, setOpenIndex] = useState(0);
  const brandColor = "#00a99d";

  const toggleItem = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // ======================================================================
  // CONTEÚDO DO MANUAL (Focado no fluxo da Real Benefícios)
  // ======================================================================
  const helpContent = [
    {
      title: "Capítulo 1: Como enviar uma Nova Solicitação?",
      icon: <PlusCircle className="w-5 h-5" />,
      content: (
        <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
          <p>
            O processo de análise começa quando você envia os dados do locatário (e dos fiadores, se houver).
          </p>
          <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-xl border border-teal-100 dark:border-teal-800/30">
            <p className="font-bold text-teal-800 dark:text-teal-300 mb-2">
              Passo a Passo Rápido:
            </p>
            <ol className="list-decimal pl-4 space-y-2">
              <li>Acesse o menu lateral e clique em <strong>Nova Solicitação</strong>.</li>
              <li>Preencha os dados básicos do Imóvel (Valor do Aluguel, Condomínio, IPTU e Endereço).</li>
              <li>Adicione as partes envolvidas (Locatário Principal e/ou Fiadores/Cônjuges).</li>
              <li>Faça o upload dos documentos obrigatórios em formato <strong>PDF</strong>.</li>
              <li>Revise as informações e clique em "Enviar para Análise". O protocolo será gerado instantaneamente.</li>
            </ol>
          </div>
        </div>
      ),
    },
    {
      title: "Capítulo 2: Documentos Aceitos para Análise",
      icon: <ShieldCheck className="w-5 h-5" />,
      content: (
        <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
          <p>
            Nossa plataforma utiliza inteligência artificial e a integração com a <strong>FC Análises</strong> para validar documentos. Arquivos ilegíveis causarão atraso (status Pendente).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
              <p className="font-bold text-slate-800 dark:text-white mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-brand-primary" /> Identificação
              </p>
              <ul className="list-disc pl-4 space-y-1 text-xs sm:text-sm">
                <li>RG (Frente e Verso) ou CNH válida.</li>
                <li>Documento deve estar fora do plástico, legível e sem reflexos.</li>
              </ul>
            </div>
            
            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
              <p className="font-bold text-slate-800 dark:text-white mb-2 flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-brand-primary" /> Comprovação de Renda
              </p>
              <ul className="list-disc pl-4 space-y-1 text-xs sm:text-sm">
                <li>Assalariados: Últimos 3 holerites.</li>
                <li>Autônomos: Extrato bancário completo (últimos 3 meses) ou Imposto de Renda.</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Capítulo 3: Entendendo os Status da Ficha",
      icon: <Activity className="w-5 h-5" />,
      content: (
        <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
          <p>Acompanhe o andamento das suas fichas pela <strong>Caixa de Entrada (Solicitações)</strong>. Entenda o que cada etiqueta significa:</p>
          <ul className="space-y-3 mt-3">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 border border-blue-200 shrink-0 w-24 justify-center">EM ANÁLISE</span>
              <span className="text-xs sm:text-sm">A ficha foi recebida e nossa equipe está rodando a análise antifraude e o motor de crédito.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-yellow-100 text-yellow-700 border border-yellow-200 shrink-0 w-24 justify-center">PENDENTE</span>
              <span className="text-xs sm:text-sm">Precisamos da sua atenção. Geralmente falta um documento ou alguma imagem ficou ilegível. Verifique o "Parecer" dentro da ficha.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-700 border border-green-200 shrink-0 w-24 justify-center">APROVADA</span>
              <span className="text-xs sm:text-sm">Crédito aprovado! A ficha está liberada para a geração do contrato digital.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 border border-red-200 shrink-0 w-24 justify-center">REJEITADA</span>
              <span className="text-xs sm:text-sm">Infelizmente, o perfil não atingiu os critérios de risco para a emissão da garantia locatícia.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-green-600 text-white border border-green-700 shrink-0 w-24 justify-center text-center leading-tight">GARANTIA ATIVA</span>
              <span className="text-xs sm:text-sm">O processo foi 100% concluído e o contrato digital já foi assinado por todas as partes.</span>
            </li>
          </ul>
        </div>
      ),
    },
    {
      title: "Capítulo 4: Assinatura Digital (ClickSign)",
      icon: <FileSignature className="w-5 h-5" />,
      content: (
        <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
          <div className="p-4 border-l-4 border-[#00a99d] bg-slate-50 dark:bg-slate-800/50 rounded-r-xl">
            <p>
              Quando uma ficha é <strong>Aprovada</strong>, nossa equipe dispara o Contrato de Garantia através da nossa integração oficial com a <strong>ClickSign</strong>.
            </p>
          </div>
          <p>
            <strong>Como funciona o fluxo de assinaturas?</strong>
          </p>
          <ul className="list-disc pl-4 space-y-1">
            <li>A plataforma monta a "Minuta" automaticamente com os dados preenchidos na solicitação.</li>
            <li>Um e-mail da ClickSign chega na caixa de entrada do Locatário e dos Fiadores (se houver).</li>
            <li>Eles assinam o documento digitalmente com validade jurídica (usando token via e-mail/WhatsApp).</li>
            <li>Assim que a última assinatura é coletada, o status da sua ficha muda automaticamente para <strong>Garantia Ativa</strong>.</li>
          </ul>
        </div>
      ),
    },
    {
      title: "Capítulo 5: Faturamento e Boletos",
      icon: <DollarSign className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-sm text-slate-600 dark:text-slate-300">
          <p>
            A cobrança da Fiança Locatícia é gerida de forma automatizada pelo sistema, garantindo transparência e facilidade para o inquilino.
          </p>

          <div className="bg-orange-50 dark:bg-orange-900/10 p-4 rounded-xl border border-orange-100 dark:border-orange-900/30">
            <p className="font-bold text-orange-800 dark:text-orange-300 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Importante
            </p>
            <p className="text-xs sm:text-sm">
              A emissão da fatura (Boleto/Pix) só ocorre <strong>após</strong> o contrato ter sido assinado por todas as partes e o status mudar para "Garantia Ativa". Utilizamos a integração com o Asaas para garantir o split de pagamentos e a conciliação financeira automática.
            </p>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-light-bg dark:bg-dark-bg min-h-full transition-colors duration-200 font-sans max-w-5xl mx-auto">
      
      {/* CABEÇALHO: PADRÃO REAL BENEFÍCIOS */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-light-border dark:border-dark-border pb-6 mb-8">
        <div className="flex items-center gap-4">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center shrink-0 border border-teal-100/50 dark:border-teal-800/30">
                <HelpCircle className="w-6 h-6 md:w-7 md:h-7" style={{ color: brandColor }} />
            </div>
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white">
                    Central de Ajuda
                </h1>
                <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 mt-0.5">
                    Guia de operações e suporte oficial da Real Benefícios.
                </p>
            </div>
        </div>
      </div>

      {/* ACORDEÃO (Lista de Capítulos) */}
      <div className="bg-white dark:bg-dark-card rounded-2xl shadow-sm border border-slate-200 dark:border-dark-border overflow-hidden">
        {helpContent.map((item, index) => (
          <div
            key={index}
            className="border-b border-slate-100 dark:border-dark-border last:border-0"
          >
            <button
              onClick={() => toggleItem(index)}
              className={`w-full flex items-center justify-between p-5 text-left transition-colors ${
                openIndex === index
                  ? "bg-slate-50 dark:bg-slate-800/50"
                  : "hover:bg-slate-50 dark:hover:bg-slate-800/30"
              }`}
            >
              <div className={`flex items-center gap-3 font-bold ${openIndex === index ? 'text-[#00a99d]' : 'text-slate-700 dark:text-slate-200'}`}>
                <div className={`${openIndex === index ? 'text-[#00a99d]' : 'text-slate-400 dark:text-slate-500'}`}>
                  {item.icon}
                </div>
                {item.title}
              </div>
              {openIndex === index ? (
                <ChevronUp className="w-5 h-5 text-[#00a99d]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              )}
            </button>

            {/* Conteúdo com Animação Suave */}
            {openIndex === index && (
              <div className="p-5 sm:p-6 bg-white dark:bg-dark-card animate-in slide-in-from-top-2 duration-200 border-t border-slate-100 dark:border-dark-border">
                {item.content}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* CARTÃO DE CHAMADO DE SUPORTE NO WHATSAPP */}
      <div className="mt-10 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-2xl p-8 text-center flex flex-col items-center">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
          <MessageCircle className="w-8 h-8 text-green-600 dark:text-green-500" />
        </div>
        <h3 className="text-xl font-bold text-green-800 dark:text-green-400 mb-2">
          Ainda precisa de ajuda?
        </h3>
        <p className="text-sm text-green-700 dark:text-green-300/80 mb-6 max-w-md mx-auto">
          Se o nosso guia rápido não resolveu sua dúvida, nossa equipe de suporte avançado está pronta para te atender.
        </p>
        <a
          href="https://wa.me/5548991784849"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl shadow-lg shadow-green-600/20 transition-all active:scale-95 text-sm"
        >
          <MessageCircle className="w-5 h-5" />
          Falar com Suporte (WhatsApp)
        </a>
      </div>
    </div>
  );
};

export default AjudaPage;